/**
 * Co-Pilot Chat Service
 * Manages chat state and integrates with the backend API
 */

/* global console, window, Office */

import { chatAPI, projectsAPI } from "./api.js";
import { msProjectService } from "./msProjectService.js";
import { getProjectWindowToken } from "./projectWatcher.js";
import { awaitFreshSnapshot, awaitFreshPlan, FRESH_PLAN_WAIT_MS } from "./snapshotRequest.js";
import {
  analyzeProjectHealth,
  analyzeProjectTimeline,
  formatProjectDataForChat,
  generateProjectInsights,
} from "../utils/projectHelpers.js";
import {
  payloadSnapshot,
  sentryCaptureException,
  setSentryScope,
} from "./sentry.js";
import { reportStreamFailure } from "./sentryReporting.js";
import { getModelTier } from "../utils/modelTierStore.js";
import {
  ComparisonAttachmentStore,
  attachmentKeyFor,
} from "../utils/comparisonAttachmentStore.js";

function normalizeSidecarProjectGuid(value) {
  const cleaned = String(value || "").trim().replace(/[{}]/g, "");
  const lowered = cleaned.toLowerCase();
  if (
    !cleaned ||
    lowered === "no" ||
    lowered === "none" ||
    lowered === "null" ||
    lowered === "undefined" ||
    /^0{8}-0{4}-0{4}-0{4}-0{12}$/.test(lowered)
  ) {
    return "";
  }
  return cleaned;
}

function stableGuidFromSidecarProject(activeProject) {
  const ap = activeProject || {};
  const normalizedGuid = normalizeSidecarProjectGuid(ap.ms_project_guid);
  const projectName = String(ap.project_name || "").trim() || "Untitled Project";
  const containingDir = String(ap.file_path || "").trim();
  const fullName = String(ap.file_full_name || "").trim();

  if (normalizedGuid) {
    return normalizedGuid;
  }
  if (containingDir && fullName) {
    return `path:${fullName}`;
  }
  return `sidecar-name:${projectName}`;
}

// GID-44: the IANA name the taskpane's own `.message-time` timestamps are
// rendered in (toLocaleTimeString uses the same system timezone). Sent so
// the backend can label chat-history timestamps in the LLM prompt with the
// same local time the user sees, instead of the LLM having no time context
// at all. Falls back to undefined (backend then falls back to UTC) if the
// runtime doesn't support Intl for some reason.
function resolveClientTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch (_) {
    return undefined;
  }
}

/**
 * The project_state summary sent with each chat message.
 *
 * `is_new_project` means "one task": resources used to be hard-coded empty, so
 * the old `&& resources.length === 0` never mattered. Now that the connector
 * sends the Resource Sheet (GID-215 step 1), keeping that clause would flip a
 * brand-new plan to "not new" just because it has people in it, and change
 * which prompt branch the backend takes.
 */
// The Edit engine a message gets when the user has not chosen one: Fast loop
// (the Loop engine with less thinking, GID-267). GID-295 made it the default;
// the pane's Edit engine menu preselects the same option.
export const DEFAULT_EDIT_HARNESS_MODE = "pi_manager_fast";

export function buildProjectState({ tasks = [], resources = [], assignments = [] } = {}) {
  return {
    is_new_project: tasks.length === 1,
    is_empty_gantt: tasks.length === 1,
    has_tasks: tasks.length > 1,

    has_assignments: assignments.length > 1,
    project_status: tasks.length === 1 ? "empty" : "populated",
    task_count: tasks.length,
    resource_count: resources.length,

    assignment_count: assignments.length,
  };
}

class ChatService {
  constructor() {
    this.currentSessionId = null;
    this.userId = this.generateUserId();
    this.isConnected = false;
    this.projectContext = null;
    this.lastServerPreservationSnapshot = [];
    // Comparison-file selection is owned per chat session (with a
    // pending:<projectGuid> key before the first message). A single
    // pane-global field here leaked the selection across MS Project
    // documents when the pane re-bound on a window switch.
    this.comparisonAttachments = new ComparisonAttachmentStore();
    this.currentAbortController = null;
    this.pendingFilterSyncPrefetch = null;
    this.activeProject = { guid: null, name: null };
    // GID-105 read-on-send telemetry: whether the last _awaitFreshSnapshot()
    // timed out (message went out on a possibly-stale snapshot). Sent with the
    // next message so the backend can count read-on-send timeouts in production.
    this.lastContextWasStale = false;
    this._sessionChangeListeners = new Set();
    // GID-110: bumped on every user/system navigation AWAY from the current
    // pending chat (new chat, switch, project change, delete, disconnect) —
    // NOT on _createSessionForProject's own success. Lets an in-flight
    // session-creation notice it's stale before clobbering currentSessionId
    // back onto a chat the user has since left (see _createSessionForProject).
    this._sessionGeneration = 0;
  }

  /**
   * Subscribe to session/project changes. Listener receives
   * ({ sessionId, project: {guid, name}, source }).
   */
  onSessionChange(listener) {
    if (typeof listener !== "function") return () => {};
    this._sessionChangeListeners.add(listener);
    return () => this._sessionChangeListeners.delete(listener);
  }

  _emitSessionChange(source, extras = {}) {
    const payload = {
      sessionId: this.currentSessionId,
      project: { ...this.activeProject },
      source,
      messages: extras.messages ?? null,
    };
    this._sessionChangeListeners.forEach((cb) => {
      try {
        cb(payload);
      } catch (err) {
        console.error("session-change listener threw:", err);
      }
    });
  }

  /**
   * Called once by projectWatcher when the bound MS Project document's
   * identity is locked in.
   *
   * GID-84: binding to a project always starts a NEW chat rather than
   * auto-restoring that project's most-recently-updated session. The old
   * auto-restore surfaced a stale, unrelated chat when MS Project opened a
   * brand-new blank file — unsaved files reuse default names ("Project1",
   * "Project2"...) across new-file cycles, so a fresh file could resolve to
   * the same identity as an earlier one and inherit its chat. Prior sessions
   * are still reachable via the session dropdown; we simply don't resurrect
   * one on bind. The new session itself is created lazily on the first
   * message, so empty chats never pollute the sidebar.
   *
   * The backend is the single source of truth for project↔session binding.
   * We deliberately do NOT cache the mapping locally, because Office add-in
   * panes share localStorage across all open Project windows and the cache
   * was the root cause of chats leaking between documents.
   */
  async setActiveProject({ guid, name }) {
    if (!guid) return;
    if (this.activeProject.guid === guid) {
      this.activeProject = { guid, name };
      return;
    }

    this.activeProject = { guid, name };

    // Clear stale project data — Office.context.document is process-global
    // and may still return the previous project's tasks until the next
    // fresh extraction. Clearing forces a re-read from the now-active project.
    this.projectContext = null;
    window.allProjectTasks = null;
    window._allProjectTasksWindowToken = null;
    window._allProjectTasksSnapshotTs = null;
    window._allProjectTasksMetadata = null;
    window._allProjectTasksSummaryTask = null;
    window._allProjectTasksProjectSettings = null;
    window._allProjectTasksExtras = null;

    console.log(
      "[chatService] setActiveProject " +
        JSON.stringify({ userId: this.userId, guid, name })
    );

    // Always open a fresh new chat for the newly-bound project (pending state;
    // UI shows "New chat" until the first message creates the session).
    this._sessionGeneration += 1;
    this.currentSessionId = null;
    this.isConnected = true;
    this._emitSessionChange("project_change", { messages: [] });
    return null;
  }

  /**
   * Adopt a corrected identity for the SAME physical project WITHOUT tearing
   * down the current chat.
   *
   * The pane binds at mount to whatever identity is available first. When the
   * sidecar is momentarily silent, that first identity is the Office.js GUID —
   * a documented-unreliable guess (Office.context.document is process-global and
   * differs from the sidecar's per-window truth). When the sidecar (the
   * authoritative source for this window_token) later posts, projectWatcher
   * detects a different guid for what is still the same project and calls this.
   *
   * Unlike setActiveProject (a REAL project switch, which resets to a new chat),
   * this keeps currentSessionId and the rendered messages: the guid it corrects
   * was only ever provisional, so the session the user just created — and any
   * in-flight stream bound to it — must survive. Sessions filed under the
   * provisional guid are re-keyed to the corrected one by the caller (rebind).
   */
  async applyIdentityCorrection({ guid, name }) {
    if (!guid) return null;
    if (this.activeProject.guid === guid) {
      this.activeProject = { guid, name };
      return null;
    }

    this.activeProject = { guid, name };

    // Project-data caches were keyed to the provisional identity; drop them so
    // the next context read repopulates against the corrected project. Any
    // in-flight sendMessage repopulates projectContext in
    // checkAndUpdateProjectContext before it builds its payload, so clearing
    // here cannot strip context off a message already being assembled.
    this.projectContext = null;
    window.allProjectTasks = null;
    window._allProjectTasksWindowToken = null;
    window._allProjectTasksSnapshotTs = null;
    window._allProjectTasksMetadata = null;
    window._allProjectTasksSummaryTask = null;
    window._allProjectTasksProjectSettings = null;
    window._allProjectTasksExtras = null;

    console.log(
      "[chatService] applyIdentityCorrection " +
        JSON.stringify({ userId: this.userId, guid, name, keptSession: this.currentSessionId })
    );

    // A session created before the correction was titled under the provisional
    // (office-fallback) name — e.g. "Untitled Project chat" for what is really a
    // BV5 chat. Rename it to the corrected project so the dropdown is truthful.
    // Best-effort: never block or fail the correction on it.
    if (this.currentSessionId && name) {
      const sid = this.currentSessionId;
      Promise.resolve(chatAPI.updateSession(sid, { title: `${name} chat` })).catch(() => {});
    }

    // Non-destructive: emit WITHOUT a messages payload so the history renderer
    // skips its wipe (see taskpane onSessionChange) and the in-view chat and
    // in-flight stream are preserved. currentSessionId is deliberately untouched.
    this._emitSessionChange("identity_correction");
    return null;
  }

  /**
   * @returns {Promise<{session_id: string}>} the created session — ALWAYS
   *   returned, even when the user has since navigated away from this
   *   pending chat (see the generation check below). Callers that need
   *   THIS request bound to the right session (sendMessage) must use the
   *   returned id directly rather than re-reading currentSessionId
   *   afterward, which may no longer point at it.
   */
  async _createSessionForProject(guid, name) {
    // A comparison file uploaded before the first message lives under the
    // pending key; capture it before the session id exists.
    const pendingKey = attachmentKeyFor(null, guid);
    // GID-110: snapshot before the await. chatAPI.createSession is a real
    // network round-trip; if the user clicks "+ New chat" (or switches
    // session/project) WHILE it's in flight, generation advances. Blindly
    // adopting this session as "current" on completion would silently snap
    // currentSessionId back onto a chat the user has already left — and
    // since the "create" source isn't in taskpane's re-render allow-list,
    // the DOM stays showing wherever they navigated to while the render
    // guard (which compares against this same currentSessionId) would then
    // wrongly permit the ORIGINAL query's answer to render there.
    const generationBeforeCreate = this._sessionGeneration;
    const session = await chatAPI.createSession({
      title: name ? `${name} chat` : "New chat",
      user_id: this.userId,
      project_guid: guid || undefined,
      project_name: name || undefined,
    });
    if (this._sessionGeneration === generationBeforeCreate) {
      this.currentSessionId = session.session_id;
      this.isConnected = true;
      this.comparisonAttachments.migrate(pendingKey, session.session_id);
      if (this.comparisonAttachments.get(session.session_id)) {
        this._syncAttachmentToSession();
      }
      this._emitSessionChange("create");
    }
    return session;
  }

  /**
   * Lazily create the session for the active project on first message send.
   *
   * @returns {Promise<{session_id: string}>} the current or newly-created
   *   session — never null. Use the RETURNED id to bind a request, not a
   *   subsequent read of currentSessionId (see _createSessionForProject).
   */
  async _ensureSessionForActiveProject() {
    if (this.currentSessionId) return { session_id: this.currentSessionId };
    const { guid, name } = this.activeProject;
    return this._createSessionForProject(guid, name);
  }

  /**
   * UI entry: explicit "+ New chat" — does NOT create a session immediately.
   * Just clears the current selection so the user sees a blank "New chat"
   * state. The real session is created on the first message.
   */
  async startNewChatForActiveProject() {
    if (!this.activeProject.guid) {
      throw new Error("No active project yet");
    }
    this._sessionGeneration += 1;
    this.currentSessionId = null;
    this.isConnected = true;
    this._emitSessionChange("new_chat_pending", { messages: [] });
    return null;
  }

  /**
   * UI entry: switch to an existing session (from the selector).
   */
  async switchToSession(sessionId) {
    if (!sessionId || sessionId === this.currentSessionId) return null;
    const requestGeneration = ++this._sessionGeneration;
    const session = await chatAPI.getSession(sessionId);
    if (requestGeneration !== this._sessionGeneration) return null;
    this.currentSessionId = session.session_id;
    this.isConnected = true;
    this._seedAttachmentFromSession(session);
    this._emitSessionChange("switch", { messages: session.messages || [] });
    return session;
  }

  /**
   * Re-fetch the CURRENT session from the backend and re-render it, without
   * changing which session is current.
   *
   * The DOM can fall out of sync with the backend: an answer that finishes while
   * the user is viewing another session is saved but not painted (the render
   * guard correctly refuses to draw it into the wrong view), and a stream cut
   * mid-flight leaves the reply saved server-side but never delivered to the
   * client. Returning to such a session via the selector used to be a no-op
   * (switchToSession early-returns on the same id), so the saved answer was
   * unreachable. This makes re-selecting the current session reload it — the
   * reliable "show me what's actually saved" action. Deliberately does NOT bump
   * the generation counter: it is not a navigation away from a pending chat.
   */
  async reloadCurrentSession() {
    const sessionId = this.currentSessionId;
    if (!sessionId) return null;
    const session = await chatAPI.getSession(sessionId);
    this._seedAttachmentFromSession(session);
    this._emitSessionChange("switch", { messages: session.messages || [] });
    return session;
  }

  async listSessions({ projectGuid = null, page = 1, pageSize = 50 } = {}) {
    return chatAPI.getUserSessions(page, pageSize, projectGuid);
  }

  async renameSession(sessionId, title) {
    return chatAPI.updateSession(sessionId, { title });
  }

  async deleteSessionById(sessionId) {
    const result = await chatAPI.deleteSession(sessionId);
    this.comparisonAttachments.clear(sessionId);
    if (this.currentSessionId === sessionId) {
      this._sessionGeneration += 1;
      this.currentSessionId = null;
      this.isConnected = false;
      this._emitSessionChange("delete");
    }
    return result;
  }

  getPendingFilterSyncPrefetch() {
    return this.pendingFilterSyncPrefetch;
  }

  clearPendingFilterSyncPrefetch() {
    this.pendingFilterSyncPrefetch = null;
  }

  getStableTaskIdentity(task) {
    if (!task || typeof task !== "object") {
      return "";
    }
    return msProjectService.normalizeStableId(
      task.stableId || task.stable_id || task.Text29 || task.text29 || ""
    );
  }

  isUnsupportedOfficeField(task, fieldName) {
    const fieldDebug = task?.officeFieldDebug?.fieldReads?.[fieldName];
    if (!fieldDebug || typeof fieldDebug !== "object") {
      return false;
    }

    if (fieldDebug.status && fieldDebug.status !== "succeeded") {
      return true;
    }

    const extractedValue = fieldDebug.extractedValue;
    if (fieldName === "Marked") {
      if (msProjectService.isDurationLikeValue(extractedValue)) {
        return true;
      }
      if (extractedValue === null || extractedValue === undefined || extractedValue === "") {
        return false;
      }
      const lowered = String(extractedValue).trim().toLowerCase();
      return !["yes", "true", "1", "no", "false", "0"].includes(lowered);
    }

    if (fieldName === "Stop" || fieldName === "Resume") {
      return msProjectService.isDurationLikeValue(extractedValue);
    }

    return false;
  }

  mergeUnsupportedPreservedFields(tasks, previousTasks = []) {
    if (!Array.isArray(tasks) || tasks.length === 0 || !Array.isArray(previousTasks) || previousTasks.length === 0) {
      return tasks;
    }

    const previousByStableId = new Map();
    for (const previousTask of previousTasks) {
      const stableId = this.getStableTaskIdentity(previousTask);
      if (!stableId) {
        continue;
      }
      previousByStableId.set(stableId, previousTask);
    }

    return tasks.map((task) => {
      const stableId = this.getStableTaskIdentity(task);
      const previousTask = stableId ? previousByStableId.get(stableId) : null;
      if (!previousTask) {
        return task;
      }

      const nextTask = { ...task };
      const fallbackApplied = {};

      if (this.isUnsupportedOfficeField(task, "Marked")) {
        const previousMarked = previousTask.marked ?? previousTask.Marked ?? null;
        if (previousMarked !== null && previousMarked !== undefined) {
          nextTask.marked = previousMarked;
          fallbackApplied.marked = true;
        }
      }

      if (this.isUnsupportedOfficeField(task, "Stop") && !nextTask.stop) {
        const previousStop = previousTask.stop ?? previousTask.stop_date ?? previousTask.Stop ?? "";
        if (previousStop) {
          nextTask.stop = previousStop;
          fallbackApplied.stop = true;
        }
      }

      if (this.isUnsupportedOfficeField(task, "Resume") && !nextTask.resume) {
        const previousResume = previousTask.resume ?? previousTask.resume_date ?? previousTask.Resume ?? "";
        if (previousResume) {
          nextTask.resume = previousResume;
          fallbackApplied.resume = true;
        }
      }

      if (Object.keys(fallbackApplied).length > 0) {
        nextTask.officeFieldDebug = {
          ...(nextTask.officeFieldDebug || {}),
          preservationFallback: fallbackApplied,
        };
      }

      return nextTask;
    });
  }

  buildPreservationSnapshotFromResponse(response) {
    if (!response || typeof response !== "object") {
      return [];
    }

    const snapshot = [];
    for (const item of [...(response.groups || []), ...(response.tasks || [])]) {
      if (!item || typeof item !== "object") {
        continue;
      }
      const stableId = this.getStableTaskIdentity(item);
      if (!stableId) {
        continue;
      }
      snapshot.push({
        stableId,
        stable_id: stableId,
        Text29: stableId,
        text29: stableId,
        marked: item.marked ?? item.Marked ?? null,
        stop: item.stop ?? item.stop_date ?? item.Stop ?? "",
        resume: item.resume ?? item.resume_date ?? item.Resume ?? "",
      });
    }
    return snapshot;
  }

  getPreviousPreservationCandidates() {
    return [
      ...(this.lastServerPreservationSnapshot || []),
      ...((this.projectContext?.current_project?.tasks) || []),
    ];
  }

  /**
   * Get project data using MSProjectService with health checks
   * @param {boolean} [forceRefresh=false] - Whether to force a fresh fetch from MS Project
   * @returns {Promise<Object>} Project data
   */
  async getProjectDataSimplified(forceRefresh = false, options = {}) {
    try {
      console.log(`ChatService: getProjectDataSimplified(forceRefresh=${forceRefresh}) starting...`);
      const windowToken = getProjectWindowToken();

      // Fast Path: Use cache if available and not forcing refresh
      // This ensures initialization is fast and doesn't hang
      const cachedWindowToken = window._allProjectTasksWindowToken || null;
      const cacheMatchesWindow = !windowToken || cachedWindowToken === windowToken;
      if (!forceRefresh && cacheMatchesWindow && window.allProjectTasks && Array.isArray(window.allProjectTasks) && window.allProjectTasks.length > 0) {
        console.log(`ChatService: Using cached data (${window.allProjectTasks.length} tasks) for fast load`);
        const cachedMetadata = window._allProjectTasksMetadata || {};
        const cachedDataSource = cachedMetadata.dataSource || "cached_project_data";
        const result = {
          project_id: `ms_project_${Date.now()}`,
          fileInfo: {
            name: cachedMetadata.projectName || "Project Data",
            projectGuid: cachedMetadata.projectGuid || null,
            identitySource: cachedMetadata.identitySource || null,
            fileFullName: cachedMetadata.fileFullName || null,
            filePath: cachedMetadata.filePath || null,
          },
          properties: { name: cachedMetadata.projectName || "Project Data" },
          tasks: window.allProjectTasks,
          // Project.Tasks starts at ID 1, so the visible Project Summary Task is
          // cached beside the task list rather than living inside it. Without this
          // the fast path silently drops row 0 and the backend falls back to the
          // document name -- which is how "live.mpp" reached a comparison instead
          // of the real summary-task name "live".
          projectSummaryTask: window._allProjectTasksSummaryTask || null,
          // Cached beside the tasks for the same reason as the summary task:
          // this is the only carrier of the host's own ScheduleFromFinish, and
          // without it every insert the AI makes falls back to a full rebuild.
          project_settings: window._allProjectTasksProjectSettings || null,
          // Cached beside the tasks by the sidecar path (GID-215 step 1), so a turn
          // built from the cache still carries the Resource Sheet, the
          // assignments and the host's real calendars.
          calendars: (window._allProjectTasksExtras || {}).calendars || null,
          assignments: (window._allProjectTasksExtras || {}).assignments || [],
          resources: (window._allProjectTasksExtras || {}).resources || [],
          metadata: {
            extractedAt: new Date().toISOString(),
            dataSource: cachedDataSource,
            extraction_method:
              cachedMetadata.extraction_method ||
              (cachedDataSource === "sidecar_com" ? "sidecar_com" : "cached_project_data"),
            projectGuid: cachedMetadata.projectGuid || null,
            identitySource: cachedMetadata.identitySource || null,
            fileFullName: cachedMetadata.fileFullName || null,
            filePath: cachedMetadata.filePath || null,
            tasksCount: window.allProjectTasks.length,
            windowToken: cachedWindowToken,
            sidecarTs: cachedMetadata.sidecarTs || window._allProjectTasksSnapshotTs || null,
            sidecarAgeSeconds: cachedMetadata.sidecarAgeSeconds ?? null,
            tasksMatchWindow: cachedMetadata.tasksMatchWindow ?? null,
            tasksWindowToken: cachedMetadata.tasksWindowToken || null,
            field_usage: cachedMetadata.field_usage || null,
            // Cached from the sidecar path's metadata (see screen_columns there), so
            // a turn built from the cache still carries the on-screen columns.
            screen_columns: cachedMetadata.screen_columns || null,
            minutes_per_day: cachedMetadata.minutes_per_day ?? null,
            minutes_per_week: cachedMetadata.minutes_per_week ?? null,
            days_per_month: cachedMetadata.days_per_month ?? null,
            // Same field as the sidecar path below. A cached read that dropped
            // it would make a current connector look silent to the sync gate
            // and rebuild the whole project instead of sending the delta.
            delta_protocol_version: cachedMetadata.delta_protocol_version ?? null,
            custom_field_info: cachedMetadata.custom_field_info || null,
            fields_read: cachedMetadata.fields_read || null,
            writeback_version: cachedMetadata.writeback_version || null,
          },
        };
        return result;
      }

      // Sidecar Path: the VSTO sidecar reads tasks via COM snapshots —
      // unlike Office.context.document which is permanently bound to the pane-
      // creation-time project). Try the backend first for sidecar task data.
      try {
        const snapshot = await projectsAPI.getActiveProject(windowToken || null);
        // Trust the sidecar snapshot whenever the sidecar actually responded (snapshot
        // present AND tasks is an array) — INCLUDING an empty [] for a genuinely blank
        // project. Post-v10 (UI-thread timer fix) the sidecar reads ActiveProject.Tasks
        // accurately, so [] means "blank project" -> empty context -> fresh GENERATION,
        // NOT a reason to fall back. Only fall back to Office.js when there is NO snapshot
        // at all (null / 404): Office.context.document is permanently bound to the
        // pane-creation-time project and goes stale after a project switch — which is
        // exactly what fed a previous project's tasks into a "blank" project as context.
        if (snapshot != null && Array.isArray(snapshot.tasks)) {
          console.log(`ChatService: Using sidecar task data (${snapshot.tasks.length} tasks) from backend COM snapshot`);
          const sidecarProject = snapshot.active_project || {};
          const sidecarProjectGuid = stableGuidFromSidecarProject(sidecarProject);
          const sidecarProjectName = sidecarProject.project_name || "Project Data";
          const projectData = {
            project_id: sidecarProjectGuid || `sidecar_${Date.now()}`,
            fileInfo: {
              name: sidecarProjectName,
              projectGuid: sidecarProjectGuid || null,
              identitySource: sidecarProjectGuid.startsWith("path:")
                ? "sidecar-path"
                : sidecarProjectGuid.startsWith("sidecar-name:")
                  ? "sidecar-name"
                  : "sidecar-guid",
              fileFullName: sidecarProject.file_full_name || null,
              filePath: sidecarProject.file_path || null,
            },
            properties: { name: sidecarProjectName },
            tasks: snapshot.tasks,
            // Project.Tasks starts at ID 1. The VSTO sidecar captures the
            // visible Project Summary Task (ID 0) separately.
            projectSummaryTask: snapshot.project_summary_task || null,
            // Field-preservation capture: calendar definitions (working times +
            // exceptions) and project scheduling settings (critical_slack_limit,
            // status_date, ...) read over COM. Forwarded verbatim -- the backend's
            // _convert_context_to_project_format reads them off current_project to
            // emit the host's REAL calendar and critical-slack threshold instead of
            // hardcoded defaults. Only the sidecar path has these; Office.js cannot
            // read any calendar data.
            calendars: snapshot.calendars || null,
            project_settings: snapshot.project_settings || null,
            // GID-215 step 1: the Resource Sheet and the task-resource
            // assignments, read over COM. Empty from connectors that predate it.
            assignments: snapshot.assignments || [],
            resources: snapshot.resources || [],
            metadata: {
              extractedAt: new Date().toISOString(),
              dataSource: "sidecar_com",
              extraction_method: "sidecar_com",
              projectGuid: sidecarProjectGuid || null,
              identitySource: sidecarProjectGuid.startsWith("path:")
                ? "sidecar-path"
                : sidecarProjectGuid.startsWith("sidecar-name:")
                  ? "sidecar-name"
                  : "sidecar-guid",
              fileFullName: sidecarProject.file_full_name || null,
              filePath: sidecarProject.file_path || null,
              tasksCount: snapshot.tasks.length,
              sidecarTs: snapshot.sidecar_ts || null,
              sidecarAgeSeconds: snapshot.age_seconds ?? null,
              windowToken: windowToken || null,
              tasksMatchWindow: snapshot.tasks_match_window !== false,
              tasksWindowToken: snapshot.tasks_window_token || null,
              field_usage: snapshot.field_usage || null,
              // The task table on screen, forwarded so the backend can send what the
              // user is looking at on every turn and tell the model what each column
              // is called. An explicit allowlist, like field_usage above: a key not
              // named here never reaches the chat request.
              screen_columns: snapshot.screen_columns || null,
              minutes_per_day: snapshot.minutes_per_day || null,
              minutes_per_week: snapshot.minutes_per_week || null,
              days_per_month: snapshot.days_per_month || null,
              // GID-43 Phase 2: the delta wire protocol version the sidecar
              // binary implements, from its compile-time constant. The backend
              // sync gate will not send a delta command stream without it —
              // absent means "old connector" and resolves to the full rebuild,
              // so dropping it here is not cosmetic. Deliberately NOT
              // connector_version: that is the installer's product version
              // (1.0.7.0) and says nothing about which verbs the binary parses.
              delta_protocol_version: snapshot.delta_protocol_version || null,
              // GID-215 step 1: per custom field alias / formula / lookup, the
              // manifest of fields read, and which write-back fields the connector
              // supports. Explicit, like every key above: unnamed keys never leave.
              custom_field_info: snapshot.custom_field_info || null,
              fields_read: snapshot.fields_read || null,
              writeback_version: snapshot.writeback_version || null,
              projectName: sidecarProjectName,
            },
          };
          window.allProjectTasks = projectData.tasks;
          window._allProjectTasksWindowToken = windowToken || null;
          window._allProjectTasksSnapshotTs = snapshot.sidecar_ts || null;
          window._allProjectTasksMetadata = projectData.metadata;
          window._allProjectTasksSummaryTask = projectData.projectSummaryTask || null;
          window._allProjectTasksProjectSettings = projectData.project_settings || null;
          window._allProjectTasksExtras = {
            resources: projectData.resources,
            assignments: projectData.assignments,
            calendars: projectData.calendars,
          };
          return projectData;
        }
        if (windowToken && snapshot?.active_project && snapshot.tasks_match_window === false) {
          console.warn(
            "ChatService: Sidecar identity matched this window, but no same-window task snapshot is available yet"
          );
        }
        console.log("ChatService: No sidecar task data — falling back to Office.js");
      } catch (sidecarErr) {
        console.warn("ChatService: Sidecar task fetch failed, falling back to Office.js:", sidecarErr);
      }

      // Office.js Fallback: used when no VSTO sidecar is running.
      // WARNING: Office.context.document is permanently bound to the pane-creation-time
      // project and may return WRONG data after a project switch.
      console.log("ChatService: Fetching project data from MSProjectService (Office.js)...");
      const projectData = await msProjectService.getProjectData({
        plannedFilterFlagField: options.plannedFilterFlagField || null,
      });

      if (projectData && Array.isArray(projectData.tasks) && projectData.tasks.length > 0) {
        try {
          if (msProjectService.getIdentityMode() !== "legacy") {
            const stableResult = await msProjectService.ensureStableIdsPresent(projectData.tasks);
            console.log(
              `ChatService: Stable ID enforcement completed (repaired=${stableResult.repaired}/${stableResult.total})`
            );
          } else {
            console.log("ChatService: IDENTITY_MODE=legacy, skipping stable-id writeback.");
          }
        } catch (stableErr) {
          console.warn(
            "ChatService: Stable ID enforcement failed, continuing with in-memory IDs only:",
            stableErr
          );
        }
      }

      // Update the global cache with the fresh data so other components can use it
      if (projectData && projectData.tasks) {
        window.allProjectTasks = projectData.tasks;
        window._allProjectTasksWindowToken = windowToken || null;
        window._allProjectTasksSnapshotTs = null;
        window._allProjectTasksMetadata = projectData.metadata || null;
        // Office.js cannot read row 0's name, so this also stops a previous
        // sidecar read's summary task from surviving into the fallback path.
        window._allProjectTasksSummaryTask = projectData.projectSummaryTask || null;
        // Same rule for the project settings: Office.js cannot read
        // ScheduleFromFinish at all, so a previous sidecar read's value must not
        // survive into this path and be reported as if it were this project's.
        window._allProjectTasksProjectSettings = projectData.project_settings || null;
        // Office.js reads no calendars, resources or assignments.
        window._allProjectTasksExtras = null;
        console.log(`ChatService: Updated global cache with ${projectData.tasks.length} fresh tasks`);
      }

      console.log("ChatService: getProjectDataSimplified() completed successfully:", {
        projectId: projectData.project_id,
        tasks: projectData.tasks.length,
        assignments: projectData.assignments.length,
        fileInfo: projectData.fileInfo
      });

      return projectData;
    } catch (error) {
      console.error("ChatService: getProjectDataSimplified() failed:", error);
      const cachedTaskCount =
        window.allProjectTasks && Array.isArray(window.allProjectTasks) ? window.allProjectTasks.length : 0;
      sentryCaptureException(error, {
        fingerprint: ["frontend_project_context_fallback", "project_data_simplified_failure"],
        level: "warning",
        tags: {
          subsystem: "chat_context",
          feature: "chat",
          operation: "getProjectDataSimplified",
        },
        contexts: {
          project_context: {
            force_refresh: Boolean(forceRefresh),
            cached_task_count: cachedTaskCount,
          },
        },
        extra: payloadSnapshot(
          {
            forceRefresh,
            cachedProjectId: window._cachedProjectId || null,
            cachedTaskCount,
          },
          "project_data_fallback"
        ),
      });

      // Fallback to cached data if fresh fetch fails
      let fallbackTasks = [];
      const windowToken = getProjectWindowToken();
      const cachedWindowToken = window._allProjectTasksWindowToken || null;
      if (
        (!windowToken || cachedWindowToken === windowToken) &&
        window.allProjectTasks &&
        Array.isArray(window.allProjectTasks)
      ) {
        fallbackTasks = window.allProjectTasks;
        console.log("ChatService: Using cached data as fallback");
      }

      return {
        project_id: `ms_project_${Date.now()}`,
        fileInfo: { name: "Unknown Project" },
        properties: { name: "Unknown Project" },
        tasks: fallbackTasks,
        assignments: [],
        resources: [],
        metadata: {
          extractedAt: new Date().toISOString(),
          error: error.message,
          dataSource: fallbackTasks.length > 0 ? "real_project_data_fallback" : "error_state",
          tasksCount: fallbackTasks.length,
        },
        retrieved_at: new Date().toISOString(),
      };
    }
  }

  /**
   * Stable per-machine user ID. Persisted in localStorage so uploaded-file
   * ownership and session tagging survive pane reloads: uploads are owned by
   * this id on the backend, and a comparison selection rehydrated from a
   * session record must still resolve its file after the pane restarts.
   * localStorage is shared across all Project windows under the add-in
   * origin — wrong for session binding (see setActiveProject), but exactly
   * right for "who is this machine's user".
   * @returns {string} User ID
   */
  generateUserId() {
    const fresh = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    try {
      const stored = window.localStorage.getItem("gs_user_id");
      if (stored) return stored;
      window.localStorage.setItem("gs_user_id", fresh);
    } catch (err) {
      // Storage unavailable (sandbox/quota) — fall back to per-load id.
    }
    return fresh;
  }

  /**
   * Initialize the chat service
   * @param {Object} options - Initialization options
   * @param {Object} [options.projectContext] - Project context data
   * @returns {Promise<void>}
   */
  async initialize() {
    // Session creation is deferred to projectWatcher → setActiveProject so
    // that each MS Project document binds to its own session. The explicit
    // "+ New chat" button calls startNewChatForActiveProject() afterwards.
    this.projectContext = null;
    this.isConnected = true;
    return null;
  }

  /**
   * Start monitoring the active MS Project document for changes
   */
  startDocumentMonitoring() {
    try {
    } catch (error) {
      console.error("Failed to start document monitoring:", error);
    }
  }

  /**
   * Handle document change events
   * @param {Object} changeInfo - Document change information
   */
  async handleDocumentChange(changeInfo) {
    try {
      if (changeInfo.type === "document_changed") {
        // OPTIMIZATION: Don't refresh context here - it will be refreshed
        // automatically before the next message via checkAndUpdateProjectContext()
        console.log("Document changed - context will refresh on next message");

        if (this.onDocumentChange) {
          this.onDocumentChange(changeInfo);
        }
      }
    } catch (error) {
      console.error("Error handling document change:", error);
    }
  }

  /**
   * Get project context
   * @returns {Promise<Object>} Project context
   */
  async getProjectContext() {
    try {
      setSentryScope({ subsystem: "chat_context", feature: "chat" });
      const projectData = await this.getProjectDataSimplified();
      console.log(
        "ChatService: getProjectDataSimplified() completed:",
        projectData ? "Success" : "Failed"
      );

      if (!projectData) {
        console.warn("ChatService: No project data available");
        return null;
      }

      const healthAnalysis = analyzeProjectHealth(projectData);
      const timelineAnalysis = analyzeProjectTimeline(projectData);
      const projectInsights = generateProjectInsights(projectData);
      const formattedData = formatProjectDataForChat(projectData);
      const previousTasks = this.getPreviousPreservationCandidates();

      const allTasks = projectData.tasks || [];
      const firstTask = allTasks[0];
      const projectSummaryTask =
        projectData.projectSummaryTask ||
        projectData.project_summary_task ||
        allTasks.find((task) => {
          const rowId = task?.index ?? task?.id;
          return (
            Number(rowId) === 0 &&
            Number(task?.outlineLevel ?? task?.outline_level) === 0
          );
        }) ||
        null;
      // Project.Name identifies the document. ProjectSummaryTask.Name is a
      // separate task-level label and must not be inferred from the first WBS row.
      const projectName = projectData.properties?.name || "Project Data";
      // Remove curly braces from GUID
      const cleanId = (id) => id ? String(id).replace(/[{}]/g, '') : id;
      const projectId =
        projectData.fileInfo?.projectGuid ||
        projectData.metadata?.projectGuid ||
        cleanId(firstTask?.id) ||
        projectData.project_id;

      // Filter out the Project Summary Task, so a blank project is truly sent
      // as an empty context.
      //
      // Identified by its ROW NUMBER, which is the only field that identifies
      // it. This used to also drop every row with `outlineLevel === 0`, which
      // could never be the project summary and could only ever be a lie:
      // `ReadTaskList` enumerates `project.Tasks`, which does not contain the
      // project summary row -- that is read separately by
      // `ReadProjectSummaryTask` -- and the Office.js path coerces the field
      // with `|| 1`. What DID produce a 0 was the sidecar's SafeInt wrapper,
      // which returns 0 on any failed COM read. Under the shipped `rebuild`
      // engine this context IS the project, so a row missing from it is a row
      // missing from the CLEAR_ALL-and-re-add stream: one COM hiccup deleted a
      // task from the user's file and the sync reported success.
      //
      // The connector now states an unreadable level as null (the
      // SafeIntOrNull pattern `index` has used since the same collision was
      // found there), and the backend refuses such a snapshot outright rather
      // than placing the row by guess. Both halves are needed: dropping the
      // row here would hide the refusal.
      //
      // A row that already carries a stable id keeps it. Coining a new one from the
      // task GUID looks harmless but is not: Project re-mints every GUID when it
      // writes a new file, so a derived id changes on Save As and comparison can no
      // longer tell two versions of one plan apart. Only an unstamped row gets a
      // derived id, and a duplicate gets its own so two rows never share one -
      // predecessor resolution maps stable id to row and a collision would point a
      // dependency at the wrong task.
      const seenStableIds = new Set();
      const filteredTasks = allTasks
        .filter((t) => t && t.index !== 0)
        .map((t, idx) => {
          let resolvedStableId = msProjectService.deriveStableId(
            t.stableId || t.stable_id || msProjectService.readTaskText29(t),
            t.guid || t.id,
            idx
          );
          if (seenStableIds.has(String(resolvedStableId).toLowerCase())) {
            resolvedStableId = msProjectService.deriveStableId("", t.guid || t.id, idx);
            if (seenStableIds.has(String(resolvedStableId).toLowerCase())) {
              resolvedStableId = msProjectService.createGeneratedStableId();
            }
          }
          seenStableIds.add(String(resolvedStableId).toLowerCase());
          return {
            ...t,
            stableId: resolvedStableId,
            stable_id: resolvedStableId,
            Text29: resolvedStableId,
            text29: resolvedStableId,
          };
        });
      const mergedTasks = this.mergeUnsupportedPreservedFields(filteredTasks, previousTasks);

      const projectContext = {
        current_project: {
          ...projectData.properties,
          name: projectName,
          project_id: projectId,
          project_tracker_id: projectData.project_id,
          file_info: projectData.fileInfo,
          project_summary_task: projectSummaryTask,
          // The host's own project-level scheduling settings, read over COM by
          // the sidecar. `_convert_context_to_project_format` reads them off
          // current_project (project_contexts_service.py :1303) and nowhere
          // else, so dropping them here is not cosmetic:
          // metadata.schedule_from_start is the ONLY evidence the delta engine
          // has that ConstraintType 0 is the default a newly created row
          // already holds, and without it EVERY insert -- a new task, a new
          // phase -- falls back to a whole-project rebuild that renumbers every
          // UniqueID (GID-43 Phase 3; `insert_unexpressible`). They were
          // captured, carried to the pane, and then dropped one function short
          // of the request.
          project_settings: projectData.project_settings || null,
          // The host's calendars, read over COM. They were read by the sidecar
          // path and then dropped here, one function short of the request, so
          // the backend's calendar handling never saw them.
          calendars: projectData.calendars || null,
          tasks: mergedTasks,
          assignments: projectData.assignments || [],
          resources: projectData.resources || [],
          metadata: {
            source: "ms_project",
            extraction_method: "office_js_api",
            last_updated: projectData.extracted_at,
            total_tasks: projectData.tasks?.length || 0,
            total_resources: projectData.resources?.length || 0,
            total_assignments: projectData.assignments?.length || 0,
            ...projectData.metadata,
          },
          analysis: {
            health: healthAnalysis,
            timeline: timelineAnalysis,
            insights: projectInsights,
            formatted_summary: formattedData,
          },
        },
        available_projects: [],
        user_permissions: ["read", "write"],
        project_statistics: healthAnalysis.statistics,
      };

      return projectContext;
    } catch (error) {
      console.warn("Failed to get project context:", error);
      sentryCaptureException(error, {
        fingerprint: ["frontend_project_context_fallback", "project_context_failure"],
        level: "warning",
        tags: {
          subsystem: "chat_context",
          feature: "chat",
          operation: "getProjectContext",
        },
        extra: payloadSnapshot(
          {
            currentSessionId: this.currentSessionId,
            userId: this.userId,
            hasCachedTasks: Boolean(window.allProjectTasks && window.allProjectTasks.length > 0),
          },
          "project_context_error"
        ),
      });

      const cachedData = null;
      if (cachedData) {
        return {
          current_project: {
            project_id: cachedData.project_id,
            project_name: cachedData.properties.name || "Cached Project",
            tasks: cachedData.tasks,
            resources: cachedData.resources,
            assignments: cachedData.assignments,
            metadata: {
              source: "ms_project",
              extraction_method: "cached_data",
              last_updated: cachedData.extracted_at,
            },
          },
          available_projects: [],
          user_permissions: ["read", "write"],
        };
      }

      return null;
    }
  }

  /**
   * Check if project has changed and update context if needed
   * @returns {Promise<boolean>} True if project changed, false if same project
   */
  /**
   * GID-105: request a fresh sidecar COM read and wait (briefly) for it to land.
   * Resolves as soon as the backend reports our request fulfilled, or after a
   * short timeout (then the caller uses whatever snapshot already exists). Never
   * throws — a send must not be blocked by this.
   * @param {number} timeoutMs
   * @param {number} pollMs
   */
  async _awaitFreshSnapshot(timeoutMs = 4000, pollMs = 250) {
    const fresh = await awaitFreshSnapshot({
      requestSnapshot: (wt) => projectsAPI.requestSnapshot(wt || null),
      getSnapshotRequestStatus: (wt) => projectsAPI.getSnapshotRequestStatus(wt || null),
      windowToken: getProjectWindowToken() || null,
      timeoutMs,
      pollMs,
    });
    if (!fresh) {
      console.warn("[read-on-send] sent on STALE context - fresh read did not land in", timeoutMs, "ms");
    }
    this.lastContextWasStale = !fresh;
    return fresh;
  }

  /** How long a send waits for the connector's fresh read (a method so tests can shorten it). */
  _freshPlanWaitMs() {
    return FRESH_PLAN_WAIT_MS;
  }

  /**
   * Ask the connector for a fresh read and wait for it (up to 20s). Records the
   * outcome; see awaitFreshPlan for the statuses.
   * @param {{onWaiting?: Function}} [options]
   */
  async _awaitFreshPlan({ onWaiting = null } = {}) {
    const result = await awaitFreshPlan({
      requestSnapshot: (wt) => projectsAPI.requestSnapshot(wt || null),
      getSnapshotRequestStatus: (wt) => projectsAPI.getSnapshotRequestStatus(wt || null),
      windowToken: getProjectWindowToken() || null,
      timeoutMs: this._freshPlanWaitMs(),
      onWaiting,
    });
    this.lastContextWasStale = result.status !== "fresh";
    if (this.lastContextWasStale) {
      console.warn("[read-on-send] no fresh plan:", result.status);
    }
    return result;
  }

  /** The error that stops a send when no fresh plan could be read. */
  async _planNotFreshError(result) {
    let planReadAt = null;
    try {
      const identity = await projectsAPI.getActiveProjectIdentity(getProjectWindowToken() || null);
      // /projects/active reports when the held plan was read as sidecar_ts.
      planReadAt = identity?.sidecar_ts || null;
    } catch {
      planReadAt = null;
    }
    const error = new Error(`No fresh plan from Microsoft Project (${result.status})`);
    error.type = "plan_not_fresh";
    error.reason = result.status;
    error.connectorSeenSecondsAgo = result.connectorSeenSecondsAgo ?? null;
    error.planReadAt = planReadAt;
    error.waitedMs = Number.isFinite(result.waitedMs) ? result.waitedMs : null;
    return error;
  }

  /**
   * @param {string|null} plannedFilterFlagField
   * @param {{allowStalePlan?: boolean, onPlanStatus?: (status: "waiting"|"fresh") => void}} [options]
   *   allowStalePlan: the user chose to send on the plan the backend already
   *   holds. Without it a send with no fresh plan throws `plan_not_fresh`.
   */
  async checkAndUpdateProjectContext(plannedFilterFlagField = null, { allowStalePlan = false, onPlanStatus = null } = {}) {
    // Read-on-send: a message goes out on a plan read for it. The wait is
    // outside the try below on purpose -- its catch-all falls back to the
    // cached context, which is exactly the silent stale send this prevents.
    if (allowStalePlan) {
      // The user already chose the held plan on a not-sent notice: send now,
      // marked stale, rather than making them sit through another wait.
      this.lastContextWasStale = true;
    } else {
      const freshness = await this._awaitFreshPlan({ onWaiting: () => onPlanStatus?.("waiting") });
      if (freshness.status === "fresh") {
        onPlanStatus?.("fresh");
      } else if (freshness.status !== "unsupported") {
        throw await this._planNotFreshError(freshness);
      }
    }
    try {
      // Force a fresh fetch here to ensure we have the latest data before sending a message
      const currentProjectData = await this.getProjectDataSimplified(true, {
        plannedFilterFlagField,
      });
      this.pendingFilterSyncPrefetch = currentProjectData?.filter_sync_prefetch || {
        planned_flag_field: plannedFilterFlagField || null,
        by_guid: {},
        total_tasks: Array.isArray(currentProjectData?.tasks) ? currentProjectData.tasks.length : 0,
        read_failures: 0,
        complete: false,
        exhausted: !plannedFilterFlagField,
        source: plannedFilterFlagField ? "missing_prefetch" : "session_exhausted",
      };
      const currentProjectId = currentProjectData.project_id;
      const currentProjectName = currentProjectData.properties.name;

      const previousProjectId = this.projectContext?.current_project?.project_id;

      if (!previousProjectId || previousProjectId !== currentProjectId) {
        this.projectContext = await this.getProjectContext();

        if (previousProjectId) {
          const changeMessage = `Project changed from "${this.projectContext?.current_project?.project_name}" to "${currentProjectName}". The user's question is about the new project data.`;

          this.lastProjectChangeMessage = changeMessage;
        }

        return true;
      }

      // Even if project ID hasn't changed, the content might have (e.g. new tasks)
      // So we should update the context with the fresh data we just fetched
      this.projectContext = await this.getProjectContext();

      return false;
    } catch (error) {
      console.error("Error checking project context:", error);
      this.pendingFilterSyncPrefetch = {
        planned_flag_field: plannedFilterFlagField || null,
        by_guid: {},
        total_tasks: 0,
        read_failures: 0,
        complete: false,
        exhausted: !plannedFilterFlagField,
        source: "fresh_fetch_failed",
      };
      this.projectContext = await this.getProjectContext();
      return true;
    }
  }

  /**
   * Send a message to the chat
   * @param {string} message - Message text
   * @param {Object} options - Send options
   * @param {boolean} [options.stream=true] - Whether to use streaming
   * @param {Function} [options.onChunk] - Callback for streaming chunks
   * @param {Function} [options.onComplete] - Callback when complete
   * @param {Function} [options.onError] - Callback for errors
   * @param {"current"|"pi_manager"|"pi_manager_fast"|"pi_manager_old"} [options.editHarnessMode=DEFAULT_EDIT_HARNESS_MODE] - Gantt edit runtime (pi_manager_fast: Loop at low thinking, GID-267; pi_manager_old: TEMPORARY(old-pi-ab))
   * @param {"full_report"} [options.comparisonAction] - Explicit comparison action
   * @returns {Promise<Object>} Response data
   */
  async sendMessage(message, options = {}) {
    if (!this.isConnected) {
      throw new Error("Chat service not initialized");
    }

    // GID-107/GID-110: bind this request to whichever session it actually
    // belongs to. When one already exists, currentSessionId is read
    // synchronously here (safe — no await in between). When one must be
    // created, use _ensureSessionForActiveProject's RETURNED session id,
    // not a later re-read of currentSessionId: that field can get silently
    // reset if the user navigates away (new chat / switch / project change)
    // DURING the ensure call's own createSession() await (see
    // _createSessionForProject) — re-reading it here would then either
    // throw needlessly or bind to whatever the user has since navigated to
    // instead of the session this send actually created.
    let boundSessionId = this.currentSessionId;
    if (!boundSessionId) {
      const ensuredSession = await this._ensureSessionForActiveProject();
      boundSessionId = ensuredSession?.session_id || null;
      if (!boundSessionId) {
        throw new Error("Chat service not initialized");
      }
    }

    // Freeze happens above; hand the frozen value to the caller's render
    // guard, synchronously, before the awaits below can yield to a switch.
    options.onSessionBound?.(boundSessionId);

    const projectChanged = await this.checkAndUpdateProjectContext(
      options.plannedFilterFlagField || null,
      { allowStalePlan: options.allowStalePlan === true, onPlanStatus: options.onPlanStatus || null }
    );

    // Guard: Office.context.document is fixed to the pane's original window.
    // If the sidecar has switched the session to a different project, the cached
    // context belongs to the wrong project — sending it would give the AI tasks
    // from the old project. Null it out so the backend responds without stale data.
    const ctxProjectName = this.projectContext?.current_project?.name?.trim() || "";
    const activeProjectName = (this.activeProject?.name || "").trim();
    // Cross-contamination from Office.context.document (process-global) is
    // handled by clearing the cache in setActiveProject() when the sidecar
    // detects a project switch.  The name-based guard is disabled because
    // Office.js returns the project Title (e.g. Hebrew) while the sidecar
    // returns the file name (English) — they legitimately differ for the
    // SAME project, causing false suppression on every message.
    const contextMismatch = false;
    if (contextMismatch) {
      console.warn(
        `[chatService] project_context mismatch: context="${ctxProjectName}" active="${activeProjectName}" — suppressing stale context`
      );
    }

    const messageData = {
      message: message.trim(),
      session_id: boundSessionId,
      user_id: this.userId,
      project_context: contextMismatch ? null : this.projectContext,
      stream: false,
      provider: "gemini",
      model_tier: getModelTier(),
      edit_harness_mode: String(options.editHarnessMode || DEFAULT_EDIT_HARNESS_MODE).trim(),
      timezone: resolveClientTimezone(),
      context_was_stale: this.lastContextWasStale === true,
    };
    const routeOverride = String(options.routeOverride || "auto").trim();
    if (routeOverride && routeOverride !== "auto") {
      messageData.route_override = routeOverride;
    }

    const comparisonAttachment = this.getComparisonAttachment(boundSessionId);
    if (comparisonAttachment) {
      messageData.comparison_attachment = comparisonAttachment;
    }
    if (options.comparisonAction === "full_report") {
      messageData.comparison_action = options.comparisonAction;
    }

    if (!contextMismatch && this.projectContext && this.projectContext.current_project) {
      const tasks = this.projectContext.current_project.tasks || [];
      const resources = this.projectContext.current_project.resources || [];
      const assignments = this.projectContext.current_project.assignments || [];

      messageData.project_state = {
        ...buildProjectState({ tasks, resources, assignments }),
        is_server_project: this.projectContext.current_project.file_info?.isServerProject || false,
        project_guid:
          this.activeProject?.guid ||
          this.projectContext.current_project.file_info?.projectGuid ||
          this.projectContext.current_project.metadata?.projectGuid ||
          null,
        last_updated: this.projectContext.current_project.metadata?.last_updated || null
      };
    }

    if (projectChanged && this.lastProjectChangeMessage) {
      messageData.project_change_notification = this.lastProjectChangeMessage;
      this.lastProjectChangeMessage = null;
    }

    if (this.projectContext) {
    } else {
      console.warn("No project context available when sending message");
    }

    try {
      const tasks = messageData.project_context?.current_project?.tasks || [];
      if (Array.isArray(tasks) && tasks.length > 0) {
        messageData.project_context = {
          ...(messageData.project_context || {}),
          current_project: {
            ...((messageData.project_context || {}).current_project || {}),
            tasks: tasks.map((t) => ({
              ...t,
              start: t.start ?? t.Start ?? null,
              finish: t.finish ?? t.Finish ?? null,
              duration: t.duration ?? t.Duration ?? null,
              active: t.active ?? t.isActive ?? t.Active ?? true,
              isActive: t.isActive ?? t.active ?? t.Active ?? true,
              baselineDuration: t.baselineDuration ?? t.BaselineDuration ?? "0d",
              baselineStart: t.baselineStart ?? t.BaselineStart ?? "",
              baselineFinish: t.baselineFinish ?? t.BaselineFinish ?? "",
              constraintDate: t.constraintDate ?? t.ConstraintDate ?? "",
              constraintType: t.constraintType ?? t.ConstraintType ?? 0,
              totalSlack: t.totalSlack ?? t.TotalSlack ?? null,
              freeSlack: t.freeSlack ?? t.FreeSlack ?? null,
              finishVariance: t.finishVariance ?? t.FinishVariance ?? null,
              isCritical: t.isCritical ?? t.is_critical ?? t.critical ?? t.Critical ?? null,
              percentComplete: t.percentComplete ?? t.PercentComplete ?? 0,
              actualStart: t.actualStart ?? t.ActualStart ?? "",
              active: t.active ?? t.isActive ?? t.Active ?? true,
              marked: t.marked ?? t.Marked ?? null,
              notes: t.notes ?? t.Notes ?? t.description ?? "",
              resourceNames: t.resourceNames ?? t.resource_names ?? t.ResourceNames ?? "",
              stop: t.stop ?? t.Stop ?? "",
              resume: t.resume ?? t.Resume ?? "",
              actualFinish: t.actualFinish ?? t.ActualFinish ?? "",
              marked: t.marked ?? t.Marked ?? null,
              notes: t.notes ?? t.Notes ?? "",
              resourceNames: t.resourceNames ?? t.resource_names ?? t.ResourceNames ?? "",
              textFields: t.textFields ?? t.text_fields ?? {},
              text_fields: t.text_fields ?? t.textFields ?? {},
              // ABSENT is not the same claim as NULL, and `??` cannot tell
              // them apart. A producer that never emitted the field is making
              // no claim and keeps the level-1 default; a connector that
              // emitted it as null is stating that Microsoft Project could not
              // report the row's level, and coercing that to 1 would place the
              // row at the top of the project — silently re-parenting it on
              // the rebuild, and hiding the backend's refusal of exactly this.
              outlineLevel:
                t.outlineLevel !== undefined
                  ? t.outlineLevel
                  : t.OutlineLevel !== undefined
                    ? t.OutlineLevel
                    : 1,
              predecessors: t.predecessors ?? t.Predecessors ?? "",
              successors: t.successors ?? t.Successors ?? "",
              textFields: t.textFields ?? t.text_fields ?? {},
              text_fields: t.text_fields ?? t.textFields ?? {},
              stableId: t.stableId ?? t.stable_id ?? t.Text29 ?? "",
              stable_id: t.stable_id ?? t.stableId ?? t.Text29 ?? "",
              Text29: t.Text29 ?? t.text29 ?? t.stableId ?? t.stable_id ?? "",
              text29: t.text29 ?? t.Text29 ?? t.stableId ?? t.stable_id ?? "",
            })),
          },
        };
      }
    } catch (e) {
      console.warn("Failed to enrich project context tasks:", e);
    }

    try {
      if (options.stream) {
        return await this.sendStreamingMessage(messageData, options);
      } else {
        return await this.sendRegularMessage(messageData);
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      throw error;
    }
  }

  /**
   * Send a regular (non-streaming) message
   * @param {Object} messageData - Message data
   * @returns {Promise<Object>} Response data
   */
  async sendRegularMessage(messageData) {
    this.currentAbortController = new AbortController();
    try {
      const response = await chatAPI.sendMessage(messageData, this.currentAbortController.signal);
      this._adoptRedirectedSession(messageData.session_id, response.session_id);
      this.lastServerPreservationSnapshot = this.buildPreservationSnapshotFromResponse(response);
      return response;
    } finally {
      this.currentAbortController = null;
    }
  }

  /**
   * GID-107: the backend can silently redirect a message to a freshly
   * created, correctly-scoped session instead of the one we sent
   * (processor.get_or_create_session's project_guid-mismatch fallback —
   * e.g. a session picked via "All projects" belonged to a different
   * project than the one actually open). Adopt the redirected id so (a) a
   * render guard comparing against the live currentSessionId matches and
   * the response actually renders, and (b) the next message uses the
   * corrected session instead of repeating the same mismatch forever.
   *
   * Compares against the id WE SENT (boundSessionId), never the live
   * currentSessionId — a legitimate user switch away mid-request must NOT
   * be reverted here (that is GID-106's job, not this one's).
   */
  _adoptRedirectedSession(sentSessionId, responseSessionId) {
    if (!responseSessionId || responseSessionId === sentSessionId) return;
    this.currentSessionId = responseSessionId;
    this._emitSessionChange("redirected");
  }

  abort() {
    this.currentAbortController?.abort();
    this.currentAbortController = null;
  }

  /**
   * Send a streaming message
   * @param {Object} messageData - Message data
   * @param {Object} options - Stream options
   * @returns {Promise<Object>} Final response data
   */
  async sendStreamingMessage(messageData, options = {}) {
    let fullResponse = "";
    let messageId = null;
    const abortController = new AbortController();
    this.currentAbortController = abortController;
    // GID-83: one Sentry report per turn that did not end in a reply, however
    // it ended. Never the message text: only the session, trace and kind.
    let turnEnded = false;
    const reportTurnFailure = (error, traceId = null) => {
      if (turnEnded) return;
      turnEnded = true;
      reportStreamFailure(error, {
        sessionId: messageData.session_id || this.currentSessionId,
        traceId,
      });
    };

    return new Promise((resolve, reject) => {
      Promise.resolve(chatAPI.sendMessageStream(
        messageData,
        (chunk) => {
          fullResponse += chunk;
          if (options.onChunk) {
            options.onChunk(chunk, fullResponse);
          }
        },
        (data) => {
          messageId = data.message_id;

          // Must run BEFORE onComplete: the render guard's shouldRender()
          // reads currentSessionId live, so it needs the redirected value
          // in place before the caller's completion callback checks it.
          this._adoptRedirectedSession(messageData.session_id, data.session_id);

          // A failed turn ends with an error chunk rather than an HTTP error.
          if (data?.metadata?.error) {
            reportTurnFailure(
              { type: "server_error_chunk", code: data.metadata.error },
              data.metadata.langfuse_trace_id
            );
          }
          turnEnded = true;

          if (options.onComplete) {
            options.onComplete(data, fullResponse);
          }

          const finalResponse = {
            message: fullResponse,
            session_id: data.session_id,
            message_id: messageId,
            role: "assistant",
            metadata: data.metadata || null,
            token_usage: data.token_usage || null,
            created_at: new Date().toISOString(),
          };

          this.lastServerPreservationSnapshot = this.buildPreservationSnapshotFromResponse(finalResponse);
          this.currentAbortController = null;
          resolve(finalResponse);
        },
        (error) => {
          console.error("Streaming error:", error);
          reportTurnFailure(error);
          this.currentAbortController = null;
          if (options.onError) {
            options.onError(error);
          }
          reject(error);
        },
        (data) => {
          if (options.onEvent) {
            options.onEvent(data);
          }
        },
        abortController.signal
      )).then(() => {
        // api.js returns without calling either callback when the stream
        // ends after some data but before its completion marker: the
        // connection was cut mid-reply.
        if (!turnEnded) reportTurnFailure({ type: "stream_incomplete" });
      }).catch((error) => {
        reportTurnFailure(error);
        this.currentAbortController = null;
        reject(error);
      });
    });
  }


  /**
   * Update project context
   * @param {Object} context - New project context
   */
  updateProjectContext(context) {
    this.projectContext = context;
  }

  /**
   * Refresh project context
   * @returns {Promise<Object>} Updated project context
   */
  async refreshProjectContext() {
    try {
      const newContext = await this.getProjectContext();

      if (newContext) {
        this.projectContext = newContext;
      }

      return newContext;
    } catch (error) {
      console.error("Failed to refresh project context:", error);
      throw error;
    }
  }

  /**
   * Get project summary for quick reference
   * @returns {Object} Project summary
   */
  getProjectSummary() {
    if (!this.projectContext || !this.projectContext.current_project) {
      return null;
    }

    const project = this.projectContext.current_project;

    if (project.analysis && project.analysis.formatted_summary) {
      return {
        project_id: project.project_id,
        ...project.analysis.formatted_summary.project_info,
        ...project.analysis.formatted_summary.task_summary,
        ...project.analysis.formatted_summary.resource_summary,
        ...project.analysis.formatted_summary.key_metrics,
        last_updated: project.metadata?.last_updated,
      };
    }

    const stats = this.projectContext.project_statistics || {};
    return {
      project_id: project.project_id,
      name: project.project_name,
      title: project.project_title,
      author: project.project_author,
      start_date: project.project_start_date,
      finish_date: project.project_finish_date,
      percent_complete: project.project_percent_complete,
      total_tasks: project.tasks?.length || 0,
      critical_tasks: stats.critical_tasks || 0,
      completed_tasks: stats.completed_tasks || 0,
      overdue_tasks: stats.overdue_tasks || 0,
      total_cost: stats.total_cost || 0,
      total_work: stats.total_work || 0,
      last_updated: project.metadata?.last_updated,
    };
  }

  /**
   * Key of the chat the user is looking at right now: the current session,
   * or the active project's pending slot before the first message.
   */
  _attachmentKey() {
    return attachmentKeyFor(this.currentSessionId, this.activeProject.guid);
  }

  /**
   * Seed the local selection from a backend-hydrated session record so the
   * badge survives pane reloads. Local state wins when both exist.
   */
  _seedAttachmentFromSession(session) {
    if (!session || !session.session_id) return;
    this.comparisonAttachments.seed(session.session_id, session.pending_comparison_attachment);
  }

  /**
   * Persist the current session's selection (or its clearing) on the session
   * record. Fire-and-forget: the local store is authoritative for this pane;
   * the server copy only needs to be there for the next pane mount.
   */
  _syncAttachmentToSession() {
    const sessionId = this.currentSessionId;
    if (!sessionId) return; // pending selection syncs when the session is created
    const attachment = this.comparisonAttachments.get(sessionId);
    chatAPI
      .updateSession(sessionId, { pending_comparison_attachment: attachment })
      .catch((err) => console.warn("comparison attachment sync failed:", err));
  }

  /**
   * Set comparison attachment metadata for the CURRENT chat session's next
   * message(s). Scoped per session so a project/window switch never carries
   * the selection into another document's chat.
   * @param {Object|null} attachment - Uploaded attachment metadata
   */
  setComparisonAttachment(attachment) {
    this.comparisonAttachments.set(this._attachmentKey(), attachment);
    this._syncAttachmentToSession();
  }

  /**
   * Clear the current chat session's comparison attachment selection
   */
  clearComparisonAttachment() {
    this.comparisonAttachments.clear(this._attachmentKey());
    this._syncAttachmentToSession();
  }

  /**
   * Get a chat session's comparison attachment metadata.
   *
   * @param {string|null} [sessionIdOverride] - use this session id instead
   *   of the live currentSessionId. sendMessage() must pass its frozen
   *   boundSessionId here: it reads live state after an await gap where a
   *   switch can change currentSessionId, and reading live would attach the
   *   NOW-viewed session/project's file to a message actually bound to the
   *   ORIGINAL session (GID-107 — the same class of bug this file's
   *   docstring already documents once for the upload path).
   * @returns {Object|null}
   */
  getComparisonAttachment(sessionIdOverride) {
    const key =
      sessionIdOverride != null
        ? attachmentKeyFor(sessionIdOverride, this.activeProject.guid)
        : this._attachmentKey();
    return this.comparisonAttachments.get(key);
  }

  /**
   * Disconnect from chat service
   */
  disconnect() {
    this._sessionGeneration += 1;
    this.isConnected = false;
    this.currentSessionId = null;
    this.comparisonAttachments.clearAll();
  }

  /**
   * Drop every account-owned cache without deleting anything on the server.
   * The active MS Project document will be rebound by ProjectWatcher after
   * the next account authenticates.
   */
  resetForAccountChange() {
    this.abort();
    this.disconnect();
    this.projectContext = null;
    this.lastServerPreservationSnapshot = [];
    this.lastProjectChangeMessage = null;
    this.pendingFilterSyncPrefetch = null;
    this.activeProject = { guid: null, name: null };

    if (typeof window !== "undefined") {
      window.allProjectTasks = null;
      window._allProjectTasksWindowToken = null;
      window._allProjectTasksSnapshotTs = null;
      window._allProjectTasksMetadata = null;
      window._allProjectTasksSummaryTask = null;
      window._allProjectTasksProjectSettings = null;
      window._allProjectTasksExtras = null;
    }
  }
}

export const chatService = new ChatService();
export default chatService;
